import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import Datepicker from 'react-datepicker';

import PrizeMoneyRangeFilter from './RangeFilters/PrizeMoneyRangeFilter';
import RatingFilter from './RangeFilters/RatingFilter';
import { tomorrowDateGmt } from '../../../../Filters/FilterComponents/DateFilter';
import CheckboxFilterSection from './CheckboxFilterSection';
import {
    FILTER_KEYS,
    FILTER_VALUES,
    DATE_VALUES,
    GENDER_VALUES,
    TEAM_TYPE_VALUES,
    UTR_TYPE_VALUES,
    UI_TEXT,
    INTERNAL_KEYS,
    FILTER_CONFIG,
    PRIZE_MONEY_OPTIONS,
    TRACKING_LABELS,
} from '../../../constants';
import { minUtrValue, maxUtrValue, maxUtrpValue } from '../../../../../../utils/constants';
import { trackEvent } from '../../../../../../components/Analytics/TrackedClick';
import { ballTypes } from '../../../../../../utils/eventConstants';
import styles from './moreFilters.scss';

const MoreFilters = (props) => {
    const {
        filters,
        handleFilterUpdate,
        isVisible,
        onClose,
        searchTypes = {}, // Add default empty object
    } = props;

    const [localFilters, setLocalFilters] = useState({});
    const [initialFilters, setInitialFilters] = useState({});

    // UI Text
    const { DATE, EVENT_TYPE, BALL_TYPE, VERIFIED, FORMAT, GENDER } = UI_TEXT.SECTION_TITLES;
    const { APPLY, CANCEL, CLEAR, CLOSE, MORE_FILTERS } = UI_TEXT.BUTTONS;
    const { CUSTOM, PAST, START_DATE, END_DATE } = UI_TEXT.DATE_LABELS;
    const { SINGLES, DOUBLES, DINGLES } = UI_TEXT.FORMAT_LABELS;
    const { MALE, FEMALE } = UI_TEXT.GENDER_LABELS;
    const { UTR_EVENT, VERIFIED_UTR_EVENT } = UI_TEXT.VERIFIED_LABELS;

    // Internal keys
    const { UTR_RANGE, PRIZE_MONEY_RANGE } = INTERNAL_KEYS;

    // Filter keys
    const {
        EVENT_TYPES,
        BALL_TYPES,
        EVENT_UTR_TYPES,
        EVENT_TEAM_TYPES,
        GENDERS,
        UTR_MIN,
        UTR_MAX,
        UTR_TEAM_TYPE,
        PRIZE_MONEY_MIN,
        PRIZE_MONEY_MAX,
        START_DATE: START_DATE_KEY,
        END_DATE: END_DATE_KEY,
        DATE_TYPE,
        UTR_FORMAT,
        DATE_RANGE,
    } = FILTER_KEYS;

    // Filter values
    const { PAST: PAST_VALUE, CUSTOM: CUSTOM_VALUE } = DATE_VALUES;
     const { MALE: MALE_VALUE, FEMALE: FEMALE_VALUE } = GENDER_VALUES;
    const { SINGLES: SINGLES_VALUE, DOUBLES: DOUBLES_VALUE, DINGLES: DINGLES_VALUE } = TEAM_TYPE_VALUES;
    const { MY_UTR, SANCTIONED } = UTR_TYPE_VALUES;
    const { OPEN_REGISTRATION, IN_PROGRESS, UPCOMING } = FILTER_VALUES;

    // Prevent background scroll when drawer is open
    useEffect(() => {
        if (isVisible) {
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = 'unset';
            };
        }
    }, [isVisible]);

    // Sync local filters with global filters when drawer opens
    useEffect(() => {
        if (isVisible) {
            // Only initialize with filters that are actually applied, not defaults
            const initialFilters = { ...filters };

            // Add UI-only state for UTR component (needed for display)
            if (filters?.[UTR_TEAM_TYPE]) {
                initialFilters[UTR_FORMAT] = filters[UTR_TEAM_TYPE];
            }

            // Add date type UI state - but don't treat system defaults as user selections
            const isSystemDefaultStartDate = filters?.[START_DATE_KEY] && moment(filters[START_DATE_KEY]).isSame(tomorrowDateGmt, 'day');
            const hasUserSelectedDates = (filters?.[START_DATE_KEY] && !isSystemDefaultStartDate) || filters?.[END_DATE_KEY];

            // Check for dateRange filter first (takes priority)
            if (filters?.[DATE_RANGE] === PAST_VALUE) {
                initialFilters[DATE_TYPE] = PAST_VALUE;
            } else if (filters?.[END_DATE_KEY] && moment(filters[END_DATE_KEY]).isSame(moment().subtract(1, 'days'), 'day')) {
                initialFilters[DATE_TYPE] = PAST_VALUE;
            } else if (hasUserSelectedDates) {
                initialFilters[DATE_TYPE] = CUSTOM_VALUE;
            }
            // If no user-selected dates, leave dateType undefined (no default selection)
            setLocalFilters(initialFilters);
            setInitialFilters(initialFilters);
        }
    }, [isVisible, filters]);

    const sanitizeFilters = (filters) => {
        // Start with empty sanitized object
        const sanitized = {};

        // Check if user has explicitly selected Custom date type
        const isCustomDateSelected = filters[DATE_TYPE] === CUSTOM_VALUE;
        const isPastDateSelected = filters[DATE_RANGE] === PAST_VALUE;

        // Process all allowed keys, not just those that exist in filters
        FILTER_CONFIG.ALLOWED_API_KEYS.forEach(key => {
            if (!FILTER_CONFIG.UI_ONLY_KEYS.includes(key)) {
                if (filters[key] !== undefined && filters[key] !== null) {
                    // Special handling for date fields
                    if (key === START_DATE_KEY || key === END_DATE_KEY) {
                        // Only include date fields if:
                        // 1. Custom date type is selected, OR
                        // 2. We have a dateRange filter (Past events)
                        if (isCustomDateSelected || isPastDateSelected) {
                            sanitized[key] = filters[key];
                        }
                        // Otherwise, explicitly exclude date fields (don't set them at all)
                    } else {
                        sanitized[key] = filters[key];
                    }
                } else if (FILTER_CONFIG.CHECKBOX_KEYS.includes(key)) {
                    sanitized[key] = {};
                }
            }
        });

        // Convert date strings to moment objects (only for non-null values)
        [START_DATE_KEY, END_DATE_KEY].forEach(key => {
            if (sanitized[key] && typeof sanitized[key] === 'string') {
                sanitized[key] = moment(sanitized[key]);
            }
        });

        return sanitized;
    };

    const handleApply = () => {
        const filtersToApply = sanitizeFilters(localFilters);

        // If Past is selected, clear eventState from global filters
        if (localFilters[DATE_RANGE] === PAST_VALUE) {
            filtersToApply.eventState = null;
        }

        // If Custom dates are selected, clear dateRange and eventState from global filters
        if (localFilters[DATE_TYPE] === CUSTOM_VALUE && !localFilters[DATE_RANGE]) {
            filtersToApply.dateRange = null;
            filtersToApply.eventState = null;
        }

        handleFilterUpdate(filtersToApply, 'submit');
        onClose();

        const { CLIENT, EVENT_NAME, MORE } = TRACKING_LABELS;
        trackEvent(EVENT_NAME, {
            filter_name: MORE,
            filter_value: filtersToApply,
            client: CLIENT,
        });
    };

    const handleClear = () => {
        setLocalFilters({});
        // Clear MoreFilters-specific fields; handle date/eventState based on current selections
        const clearedFilters = {
            eventTypes: {},
            eventUtrTypes: {},
            eventTeamTypes: {},
            genders: {},
            ballTypes: {},
            utrMin: minUtrValue,
            utrMax: maxUtrValue,
            prizeMoneyMin: null,
            prizeMoneyMax: null,
            startDate: null,
            endDate: null,
        };

        const dateTypeSelection = localFilters[DATE_TYPE];
        const isNowPlayingActive = filters?.dateRange === IN_PROGRESS && !filters?.eventState;
        const isUpcomingActive = filters?.dateRange === UPCOMING && !filters?.eventState;

        // Defaults: Open Registration
        clearedFilters.dateRange = null;
        clearedFilters.eventState = OPEN_REGISTRATION;

        // Respect Now Playing/Upcoming quick filters if date section has no explicit selection
        if (!dateTypeSelection && (isNowPlayingActive || isUpcomingActive)) {
            clearedFilters.dateRange = filters.dateRange;
            clearedFilters.eventState = null;
        }

        handleFilterUpdate(clearedFilters, 'submit');
        onClose();
    };

    const handleCancel = () => {
        setLocalFilters({ ...initialFilters });
        onClose();
    };

    const handleDateFilterChange = (key, value) => {
        setLocalFilters(prev => {
            // Handle DATE_TYPE radio button changes
            if (key === DATE_TYPE) {
                if (value === PAST_VALUE) {
                    const newState = { ...prev };
                    // Clear any existing start/end dates when using dateRange
                    delete newState[START_DATE_KEY];
                    delete newState[END_DATE_KEY];
                    const result = {
                        ...newState,
                        [DATE_TYPE]: value,
                        [DATE_RANGE]: PAST_VALUE, // Set dateRange for API compatibility
                    };
                    return result;
                }
                if (value === CUSTOM_VALUE) {
                    const newState = { ...prev };
                    // Clear dateRange when switching to custom dates
                    delete newState[DATE_RANGE];
                    const result = {
                        ...newState,
                        [DATE_TYPE]: value,
                        [START_DATE_KEY]: prev[START_DATE_KEY] || moment(),
                        [END_DATE_KEY]: prev[END_DATE_KEY] || moment().add(1, 'days'),
                    };
                    return result;
                }
                // Fallback for any other DATE_TYPE values
                const result = { ...prev, [DATE_TYPE]: value };
                return result;
            }
            // Handle direct date changes from datepicker
            if (key === START_DATE_KEY || key === END_DATE_KEY) {
                const newState = { ...prev };
                // Clear dateRange when using custom dates
                delete newState[DATE_RANGE];
                return {
                    ...newState,
                    [DATE_TYPE]: CUSTOM_VALUE, // Ensure DATE_TYPE stays as CUSTOM
                    [key]: value,
                };
            }
            // Fallback for any other key changes
            return { ...prev, [key]: value };
        });
    };

    const handleCheckboxChange = (key, value) => {
        setLocalFilters(prev => {
            const currentSection = prev[key] || {};
            const newValue = !currentSection[value];
            const newSection = { ...currentSection, [value]: newValue };

            // Remove false values and empty sections
            if (!newValue) {
                delete newSection[value];
            }
            const hasAnyTrueValues = Object.values(newSection).some(v => v);

            if (!hasAnyTrueValues) {
                const newState = { ...prev };
                delete newState[key];
                return newState;
            }

            return { ...prev, [key]: newSection };
        });
    };

    const handleSliderChange = (key, value) => {
        setLocalFilters(prev => {
            if (key === UTR_RANGE) {
                return { ...prev, [UTR_MIN]: value[0], [UTR_MAX]: value[1] };
            }
            if (key === PRIZE_MONEY_RANGE) {
                return { ...prev, [PRIZE_MONEY_MIN]: value[0], [PRIZE_MONEY_MAX]: value[1] };
            }
            return prev;
        });
    };

    const renderCustomDateInputs = () => {
        const selectedStartDate = localFilters[START_DATE_KEY] && moment(localFilters[START_DATE_KEY]).isValid()
            ? moment(localFilters[START_DATE_KEY])
            : null;

        const selectedEndDate = localFilters[END_DATE_KEY] && moment(localFilters[END_DATE_KEY]).isValid()
            ? moment(localFilters[END_DATE_KEY])
            : null;

        const handleStartDateChange = (date) => {
            const newStartDate = date ? moment(date) : null;

            // If there's an end date and the new start date is after it, adjust the end date
            if (newStartDate && selectedEndDate && newStartDate.isAfter(selectedEndDate, 'day')) {
                handleDateFilterChange(START_DATE_KEY, newStartDate);
                handleDateFilterChange(END_DATE_KEY, newStartDate.clone().add(1, 'day'));
            } else {
                handleDateFilterChange(START_DATE_KEY, newStartDate);
            }
        };

        const handleEndDateChange = (date) => {
            const newEndDate = date ? moment(date) : null;

            // If there's a start date and the new end date is before it, adjust the start date
            if (newEndDate && selectedStartDate && newEndDate.isBefore(selectedStartDate, 'day')) {
                handleDateFilterChange(END_DATE_KEY, newEndDate);
                handleDateFilterChange(START_DATE_KEY, newEndDate.clone().subtract(1, 'day'));
            } else {
                handleDateFilterChange(END_DATE_KEY, newEndDate);
            }
        };

        return (
            <div className={styles.dateInputs}>
                <div className={styles.dateInputGroup}>
                    <label className={styles.dateLabel}>{START_DATE}</label>
                    <Datepicker
                        selected={selectedStartDate}
                        onChange={handleStartDateChange}
                        className={styles.dateInput}
                        placeholderText="Select start date"
                        locale="us"
                        maxDate={selectedEndDate}
                    />
                </div>
                <div className={styles.dateInputGroup}>
                    <label className={styles.dateLabel}>{END_DATE}</label>
                    <Datepicker
                        selected={selectedEndDate}
                        onChange={handleEndDateChange}
                        className={styles.dateInput}
                        placeholderText="Select end date"
                        locale="us"
                        popperPlacement="bottom"
                        minDate={selectedStartDate}
                    />
                </div>
            </div>
        );
    };

    const renderDateSection = (backgroundClass = styles.filterSectionWhite) => {
        return (
            <div className={`${styles.filterSection} ${backgroundClass}`} key="date-section">
                <p className={styles.sectionTitle}>{DATE}</p>
                <div className={styles.radioGroup} key={`radio-group-${localFilters[DATE_TYPE] || 'none'}`}>
                    <label className={styles.radioLabel}>
                        <input
                            key={`past-${localFilters[DATE_TYPE] === PAST_VALUE}`}
                            type="radio"
                            name={DATE_TYPE}
                            value={PAST_VALUE}
                            checked={localFilters[DATE_TYPE] === PAST_VALUE}
                            onChange={(e) => {
                                handleDateFilterChange(DATE_TYPE, e.target.value);
                            }}
                        />
                        <span className={styles.radioText}>{PAST}</span>
                    </label>
                    <label className={styles.radioLabel}>
                        <input
                            key={`custom-${localFilters[DATE_TYPE] === CUSTOM_VALUE}`}
                            type="radio"
                            name={DATE_TYPE}
                            value={CUSTOM_VALUE}
                            checked={localFilters[DATE_TYPE] === CUSTOM_VALUE}
                            onChange={(e) => {
                                handleDateFilterChange(DATE_TYPE, e.target.value);
                            }}
                        />
                        <span className={styles.radioText}>{CUSTOM}</span>
                    </label>
                </div>

                {localFilters[DATE_TYPE] === CUSTOM_VALUE && renderCustomDateInputs()}
            </div>
        );
    };

    const hasTennisSelected = !!filters?.sportTypes?.tennis;
    const hasPickleballSelected = !!filters?.sportTypes?.pickleball;
    const bothSportsSelected = hasTennisSelected && hasPickleballSelected;

    const renderEventTypeSection = (backgroundClass = styles.filterSectionGrey) => {
        let eventTypes = [];
        const excludedEventsFilters = ['flex_league', 'ptt_events', 'groups_play'];
        const updatedEventTypes = searchTypes?.eventTypes?.filter((eventType) => !excludedEventsFilters.includes(eventType?.value));

        if (hasPickleballSelected && !hasTennisSelected) {
            const pickleballEventsFilters = ['tournament', 'camp', 'clinic', 'match_play', 'group_play'];
            eventTypes = updatedEventTypes?.filter((eventType) => pickleballEventsFilters.includes(eventType?.value));
        } else {
            eventTypes = updatedEventTypes;
        }

        return (
            <CheckboxFilterSection
                key="event-type-section"
                title={EVENT_TYPE}
                filterKey={EVENT_TYPES}
                options={eventTypes}
                backgroundClass={backgroundClass}
                localFilters={localFilters}
                handleCheckboxChange={handleCheckboxChange}
            />
        );
    };

    const renderBallTypeSection = (backgroundClass = styles.filterSectionWhite) => {
        return (
            <CheckboxFilterSection
                key="ball-type-section"
                title={BALL_TYPE}
                filterKey={BALL_TYPES}
                options={ballTypes}
                backgroundClass={backgroundClass}
                localFilters={localFilters}
                handleCheckboxChange={handleCheckboxChange}
            />
        );
    };

    const renderRatingSection = (backgroundClass = styles.filterSectionGrey) => {
        const shouldShowPickleball = hasPickleballSelected && !hasTennisSelected;
        const maxUtr = shouldShowPickleball ? maxUtrpValue : maxUtrValue;
        const currentMin = localFilters[UTR_MIN] || minUtrValue;
        const currentMax = localFilters[UTR_MAX]
            ? (localFilters[UTR_MAX] > maxUtr ? maxUtr : localFilters[UTR_MAX])
            : maxUtr;

        return (
            <div className={`${styles.filterSection} ${backgroundClass}`} key="utr-section">
                <RatingFilter
                    isPickleball={shouldShowPickleball}
                    min={currentMin}
                    max={currentMax}
                    onChange={(values) => handleSliderChange(UTR_RANGE, values)}
                />
            </div>
        );
    };

    const renderVerifiedSection = (backgroundClass = styles.filterSectionWhite) => {
        const verifiedOptions = [
            { value: MY_UTR, label: UTR_EVENT },
            { value: SANCTIONED, label: VERIFIED_UTR_EVENT },
        ];
        return (
            <CheckboxFilterSection
                key="verified-section"
                title={VERIFIED}
                filterKey={EVENT_UTR_TYPES}
                options={verifiedOptions}
                backgroundClass={backgroundClass}
                localFilters={localFilters}
                handleCheckboxChange={handleCheckboxChange}
            />
        );
    };

    const renderPrizeMoneySection = (backgroundClass = styles.filterSectionGrey) => {
        const prizeMoneyFilters = {
            [PRIZE_MONEY_MIN]: localFilters[PRIZE_MONEY_MIN] || 1,
            [PRIZE_MONEY_MAX]: localFilters[PRIZE_MONEY_MAX] || 7,
        };

        const prizeMoneySearchTypes = {
            prizeMoneyValues: PRIZE_MONEY_OPTIONS,
        };

        return (
            <div className={`${styles.filterSection} ${backgroundClass}`} key="prize-money-section">
                <PrizeMoneyRangeFilter
                    filters={prizeMoneyFilters}
                    searchTypes={prizeMoneySearchTypes}
                    handlePrizeMoneyChange={(values) => handleSliderChange(PRIZE_MONEY_RANGE, values)}
                />
            </div>
        );
    };

    const renderFormatSection = (backgroundClass = styles.filterSectionWhite) => {
        const formatOptions = [
            { value: SINGLES_VALUE, label: SINGLES },
            { value: DOUBLES_VALUE, label: DOUBLES },
        ];

        if (hasTennisSelected) {
            formatOptions.push({ value: DINGLES_VALUE, label: DINGLES });
        }

        return (
            <CheckboxFilterSection
                key="format-section"
                title={FORMAT}
                filterKey={EVENT_TEAM_TYPES}
                options={formatOptions}
                backgroundClass={backgroundClass}
                localFilters={localFilters}
                handleCheckboxChange={handleCheckboxChange}
            />
        );
    };

    const renderGenderSection = (backgroundClass = styles.filterSectionGrey) => {
        const genderOptions = [
            { value: MALE_VALUE, label: MALE },
            { value: FEMALE_VALUE, label: FEMALE },
        ];
        return (
            <CheckboxFilterSection
                key="gender-section"
                title={GENDER}
                filterKey={GENDERS}
                options={genderOptions}
                backgroundClass={backgroundClass}
                localFilters={localFilters}
                handleCheckboxChange={handleCheckboxChange}
            />
        );
    };

    const renderModalContent = () => {
        // Create array of all sections that could be rendered
        const showBallTypeSection = bothSportsSelected || (hasTennisSelected && !hasPickleballSelected);
        const shouldShowRatingSection = !bothSportsSelected;
        const allSections = [
            { component: renderDateSection, key: 'date', shouldRender: true },
            { component: renderEventTypeSection, key: 'eventType', shouldRender: true },
            { component: renderBallTypeSection, key: 'ballType', shouldRender: showBallTypeSection },
            { component: renderRatingSection, key: 'utr', shouldRender: shouldShowRatingSection },
            { component: renderVerifiedSection, key: 'verified', shouldRender: true },
            { component: renderPrizeMoneySection, key: 'prizeMoney', shouldRender: true },
            { component: renderFormatSection, key: 'format', shouldRender: true },
            { component: renderGenderSection, key: 'gender', shouldRender: true },
        ];

        // Filter only sections that should render and add alternating backgrounds
        const sectionsToRender = allSections
            .filter(section => section.shouldRender)
            .map((section, index) => {
                const isEven = index % 2 === 0;
                const backgroundClass = isEven ? styles.filterSectionWhite : styles.filterSectionGrey;
                return section.component(backgroundClass);
            });

        return (
            <div className={styles.filtersContent}>
                {sectionsToRender}
            </div>
        );
    };

    const renderHeader = () => (
        <div className={styles.drawerHeader} data-testid="search.more-filters.header">
            <button
                type="button"
                onClick={handleClear}
                className={styles.clearHeaderButton}
                data-testid="search.more-filters.clear-btn"
            >
                {CLEAR}
            </button>
            <p className={styles.drawerTitle} data-testid="search.more-filters.title">{MORE_FILTERS}</p>
            <button
                type="button"
                onClick={handleCancel}
                className={styles.closeButton}
                aria-label={CLOSE}
                data-testid="search.more-filters.close-btn"
            >
                <img src="/img/v2/icons/icon-close.svg" alt={CLOSE} />
            </button>
        </div>
    );

    const renderFooter = () => (
        <div className={styles.drawerFooter} data-testid="search.more-filters.footer">
            <div className={styles.footerButtons}>
                <button
                    type="button"
                    onClick={handleApply}
                    className={styles.applyButton}
                    data-testid="search.more-filters.apply-btn"
                >
                    {APPLY}
                </button>
                <button
                    type="button"
                    onClick={handleCancel}
                    className={styles.cancelButton}
                    data-testid="search.more-filters.cancel-btn"
                >
                    {CANCEL}
                </button>
            </div>
        </div>
    );

    if (!isVisible) {
        return null;
    }

    // Always use drawer for consistent experience across all screen sizes
    return (
        <div className={styles.drawerOverlay} data-testid="search.more-filters">
            <div className={styles.drawerBackdrop} onClick={onClose} data-testid="search.more-filters.backdrop" />
            <div className={styles.drawerContainer}>
                {renderHeader()}
                <div className={styles.drawerBody}>
                    {renderModalContent()}
                </div>
                {renderFooter()}
            </div>
        </div>
    );
};

MoreFilters.propTypes = {
    filters: PropTypes.instanceOf(Object),
    handleFilterUpdate: PropTypes.func,
    isVisible: PropTypes.bool,
    onClose: PropTypes.func,
    searchTypes: PropTypes.instanceOf(Object),
};

MoreFilters.defaultProps = {
    filters: {},
    handleFilterUpdate: () => {},
    isVisible: false,
    onClose: () => {},
    searchTypes: {},
};

export default MoreFilters;
